<template>
  <div id="articles-table">
    <datatable
      :key="`ArticleTable${key}`"
      :data="table"
      :loading="isBusy"
    />
  </div>
</template>

<script>
  import { mapState, mapGetters } from 'vuex';
  import _ from 'lodash';
  import moment from 'moment';

  import { INSIGHT_HOST, BODHI_HOST } from '../../config/app-const';
  import { SCORING_METHODS, SOCIALS_EXCLUDED_OUTLETS } from '../../config/const';
  import Datatable from '../datatable';

  export default {
    props: {
      query: {
        type: Object,
        required: () => true
      },
      articlesPagination : {
        type: Object,
        required: () => true
      }
    },
    components: {
      'datatable': Datatable
    },
    computed: {
      ...mapState({
        user: state => state.user.user.data,
        articles: state => state.queryBuilder.articles.articles,
        isBusy: state => state.queryBuilder.articles.articles.isBusy,
        project: state => state.projects.project.data
      }),
      ...mapGetters({
        projectCurrency: "projects/projectCurrency",
      }),
      projectId () {
        return this.$route.params.project_id;
      },
      isInsightUser() { return this.user && this.user.hasOwnProperty('carma_staff') },
      table () {
        const { pagination, articles } = this;
        const projectId = this.$route.params.project_id;

        const items = (articles.data && articles.data.data) || [];
        const hasSocials = this.project.enable_socials_data;

        const scoreColumns = [];
        if (this.project.scoring === SCORING_METHODS.CUSTOM) {
          scoreColumns.push({
            key: 'custom_score',
            label: this.project.custom_label || this.$i18n.t('articles.custom_score'),
            orderable: true,
            render: (row) => {
              return <span class="fav">{row.custom_score}</span>
            }
          });
        } else if (this.project.scoring === SCORING_METHODS.FAVOURABILITY) {
          Array.prototype.push.apply(scoreColumns, [
            {
              key: 'fav',
              label: this.$i18n.t('articles.fav'),
              orderable: true,
              render: (row) => {
                return <span class="fav">{row.fav}</span>
              }
            },
            {
              key: 'iq',
              label: this.$i18n.t('articles.iq'),
              orderable: true,
              render: (row) => {
                return <span class="iq">{row.iq}</span>
              }
            }
          ])
        }

        const socialColumns = [];
        if (this.project.enable_socials_data) {
          socialColumns.push(
            {
              key: 'followers',
              label: this.$i18n.t('articles.followers'),
              orderable: true
            },
            {
              key: 'handle_likes',
              label: this.$i18n.t('articles.likes'),
              orderable: true
            },
            {
              key: 'no_comments',
              label: this.$i18n.t('articles.no_comments'),
              orderable: true
            },
            {
              key: 'shares',
              label: this.$i18n.t('articles.shares'),
              orderable: true
            },
            {
              key: 'views',
              label: this.$i18n.t('articles.views'),
              orderable: true
            },
            {
              key: 'reactions',
              label: this.$i18n.t('articles.reactions'),
              orderable: true
            },
            {
              key: 'link_karma',
              label: this.$i18n.t('articles.link_karma'),
              orderable: true
            },
            {
              key: 'comment_karma',
              label: this.$i18n.t('articles.comment_karma'),
              orderable: true
            }
          );
        }

        const data = {
          body: items,
          resizable: true,
          columns: [
            {
              key: 'article_origin_id',
              label: this.$i18n.t('articles.id'),
              orderable: true,
              render: (row) => {
                return <span>{row.article_origin_id}</span>
              }
            },
            {
              key: 'date',
              label: this.$i18n.t('articles.date'),
              orderable: true,
              render: (row) => {
                const spanClasses = hasSocials ? 'date socials' : 'date';
                const format = hasSocials ? 'D MMM YYYY HH:mm:ss' : 'D MMM YYYY';
                return <span class={spanClasses}>{moment(row.published_at_time).format(format)}</span>
              }
            },
            {
              key: 'headline',
              label: this.$i18n.t('articles.headline'),
              orderable: true,
              style: { 'width': '150px' },
              cellContentStyle: { 'width': '150px' },
              cellStyle: { 'max-width': '150px' },
              render: (row) => {
                const link = this.isInsightUser ?
                  (row.url || `${INSIGHT_HOST}/projects/${row.project_uid}/articles/${row.article_uid}`)
                  : `${(BODHI_HOST || window.location.origin)}/analysis/editor/${projectId}/${row.article_id}`;
                return <a href={link} target="_blank" class="headline">{row.headline}</a>
              }
            },
            {
              key: 'translated_headline',
              label: this.$i18n.t('articles.translated_headline'),
              orderable: true
            },
            {
              key: 'url',
              label: this.$i18n.t('articles.url'),
              orderable: true,
              style: { 'width': '150px' },
              cellContentStyle: { 'width': '150px' },
              render: (row) => {
                let link;
                if (row.url) {
                  link = (<a href={row.url} class="url" target="_blank" rel="noopener noreferrer">{row.url}</a>)
                }
                return link;
              }
            },
            {
              key: 'insight_url',
              label: this.$i18n.t('articles.insight_url'),
              orderable: true,
              style: { 'width': '150px' },
              cellContentStyle: { 'width': '150px' },
              render: (row) => {
                const insight_url = `${INSIGHT_HOST}/projects/${row.project_uid}/articles/${row.coding_uid}`;
                const link = (<a href={insight_url} target="_blank" class="headline" v-tooltip={this.$i18n.t('articles.view_on_insight')}><i class="fa fa-external-link" aria-hidden="true"></i></a>);
                return link;
              }
            },
            {
              key: 'outlet',
              label: this.$i18n.t('articles.outlet'),
              orderable: true
            },
            {
              key: 'language',
              label: this.$i18n.t('articles.language'),
              orderable: true
            },
            {
              key: 'byline',
              label: hasSocials ? this.$i18n.t('articles.authors_handle') : this.$i18n.t('articles.byline'),
              orderable: true
            },
            {
              key: 'sources',
              label: this.$i18n.t('articles.sources'),
              orderable: true
            },
            {
              key: 'quotes',
              label: this.$i18n.t('articles.quotes'),
              orderable: false,
              render: (row) => {
                const quotes = row.quotes && row.quotes.map(q => (<li>{q.text}</li>))
                return (
                  <ul class="quotes-list">
                    {quotes}
                  </ul>
                )
              }
            },
            ...scoreColumns,
            {
              key: 'summary',
              label: this.$i18n.t('articles.summary'),
              style: { 'minWidth': '450px'},
              render: (row) => {
                return (<span v-tooltip={row.summary || '' }>{ 
                  row.summary && row.summary.length > 64 
                  ? row.summary.replace(/(\r\n|\n|\r)/gm, "").slice(0,64-3) + '...' 
                  : row.summary || (<span class="no-content">N/A</span>)
                }</span>)
              }
            },
            {
              key: 'comments',
              label: this.$i18n.tc('articles.comment', 2),
              orderable: true
            },
            {
              key: 'additional_info',
              label: this.$i18n.t('articles.additional_info'),
              orderable: true
            },
            {
              key: 'impressions',
              label: this.$i18n.t('articles.impressions'),
              orderable: true
            },
            {
              key: 'circulation',
              label: this.$i18n.t('articles.circulation'),
              orderable: true
            },
            {
              key: 'ave',
              label: `${this.$i18n.t('articles.ave')} (${this.projectCurrency})`,
              orderable: true,
              render: (row) => {
                if (row.outlet && SOCIALS_EXCLUDED_OUTLETS.indexOf(row.outlet) > -1) return;
                return row.ave;
              }
            },
            {
              key: 'categories',
              label: this.$i18n.t('articles.categories'),
              orderable: false,
              render: (row) => {
                const categories = row.categories.map(c => c.text);
                return (
                  <span>{categories.join(', ')}</span>
                );
              }
            },
            ...socialColumns,
            {
              key: 'coder_name',
              label: this.$i18n.t('articles.coder'),
              orderable: true
            },
          ],
          order: {
            column: pagination.order,
            direction: pagination.sort,
            onChange: (column, direction) => {
              this.sortTable(column, direction);
            }
          },
          toggleColumns: {
            enabled: true,
            excluded: [],
            hidden: ['article_origin_id', 'impressions', 'circulation', 'ave', 'categories', 'sources', 'quotes', 'summary', 'url', 'insight_url']
          },
        }

        if (!items.some(item => item.hasOwnProperty('ave')))
          data.columns = data.columns.filter(item => item.key !== 'ave');
        
        return data;
      }
    },
    methods: {
      sortTable (column, direction) {
        this.pagination = { ...this.pagination, ...{ order: column, sort: direction } };
      }
    },
    created () {
      this.pagination = _.cloneDeep(this.articlesPagination);
    },
    watch: {
      table: {
        handler() {
          this.key++;
        },
        deep: true,
      },
      'pagination': {
        handler(newValue, oldValue) {
          delete newValue.totalCount;
          delete newValue.totalPages;
          delete oldValue.totalCount;
          delete oldValue.totalPages;

          if(Object.keys(newValue).length > 0 && Object.keys(oldValue).length > 0 && !_.isEqual(newValue, oldValue)) {
            this.$emit('setArticlesPagination', newValue);
          }
        }
      }
    },
    data () {
      return {
        pagination: {},
        key: 0,
      }
    }
  }
</script>

<style lang="scss">
  @import "../../styles/components/coding-queries/articles-table.scss";
</style>